import {
  MEMBERSHIP_PLANS,
  MembershipPlan,
} from 'features/membership/membership.constants';
import { TRACKING_ID_PARAM } from '../../../../constants';
import {
  MEMBERSHIP_URL_PARAM_KEYS,
  MembershipQueryParams,
} from '../../membership.types';

const queryValue = (
  params: MembershipQueryParams,
  key: string,
): string | null => params[key] ?? null;

const VALID_MEMBERSHIP_PLANS = new Set<MembershipPlan>([
  MEMBERSHIP_PLANS.yearly,
  MEMBERSHIP_PLANS.proYearly,
  MEMBERSHIP_PLANS.proMonthly,
]);

export type MembershipAccountInfoQueryParams = {
  clientToken?: string;
  email: string;
  verificationToken?: string;
  userId?: number;
  plan?: MembershipPlan;
  firstName?: string;
  lastName?: string;
  upgrade: boolean;
  posthogTrackingId?: string;
};

export type MembershipExternalSuccessQueryParams = {
  clientToken?: string;
  donationSuccess: boolean;
  transaction?: string;
  membershipSuccess: boolean;
  membershipUpgrade: boolean;
};

const parseBooleanParam = (value: string | null): boolean => value === 'true';

const parseOptionalNumber = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const isMembershipPlan = (
  plan: string | undefined,
): plan is MembershipPlan =>
  Boolean(plan && VALID_MEMBERSHIP_PLANS.has(plan as MembershipPlan));

export const parseMembershipAccountInfoQueryParams = (
  params: MembershipQueryParams,
): MembershipAccountInfoQueryParams => {
  const plan = queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.plan) || undefined;

  return {
    clientToken:
      queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.clientToken) || undefined,
    email: queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.email) ?? '',
    verificationToken:
      queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.verificationToken) ||
      undefined,
    userId: parseOptionalNumber(
      queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.userId),
    ),
    plan: isMembershipPlan(plan) ? plan : undefined,
    firstName:
      queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.firstName) || undefined,
    lastName:
      queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.lastName) || undefined,
    upgrade: parseBooleanParam(
      queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.upgrade),
    ),
    posthogTrackingId: queryValue(params, TRACKING_ID_PARAM) || undefined,
  };
};

export const parseMembershipExternalSuccessQueryParams = (
  params: MembershipQueryParams,
): MembershipExternalSuccessQueryParams => ({
  clientToken:
    queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.clientToken) || undefined,
  donationSuccess: parseBooleanParam(
    queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.donationSuccess),
  ),
  transaction:
    queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.transaction) || undefined,
  membershipSuccess: parseBooleanParam(
    queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.membershipSuccess),
  ),
  membershipUpgrade: parseBooleanParam(
    queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.membershipUpgrade),
  ),
});

export const parseMembershipSuccessQueryParams = (
  params: MembershipQueryParams,
): { upgrade: boolean } => ({
  upgrade: parseBooleanParam(
    queryValue(params, MEMBERSHIP_URL_PARAM_KEYS.upgrade),
  ),
});
